import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import { toast } from "sonner";
import {
    Sparkles, Merge, CheckCircle, X, Save, RefreshCcw, Users, IdCard,
} from "lucide-react";

const TABS = [
    { key: "duplicates", label: "AI Duplicate Detection", icon: Sparkles },
    { key: "panfill", label: "PAN / GSTIN Bulk-Fill", icon: IdCard },
];

function AiDuplicateTab() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setBusy(true);
        setResult(null);
        try {
            const r = await api.post("/enrich/detect-duplicates", { include_all: true, max_clients: 100 });
            setResult(r.data);
            toast.success(`Found ${r.data.clusters.length} clusters via ${r.data.provider}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    const applyMerge = async (cluster) => {
        const canonicalId = window.prompt(
            `Which client_id should be kept as canonical?\n\nOptions: ${cluster.member_ids.join(", ")}\n\nRecommendation: keep the first one.`,
            cluster.member_ids[0]
        );
        if (!canonicalId || !cluster.member_ids.includes(canonicalId)) {
            toast.error("Please pick a valid ID from the cluster");
            return;
        }
        const canonicalName = window.prompt("Canonical name (edit if needed):", cluster.canonical_name);
        if (!canonicalName) return;
        try {
            const r = await api.post("/enrich/apply-merge", {
                canonical_id: canonicalId,
                canonical_name: canonicalName,
                member_ids: cluster.member_ids,
            });
            toast.success(`Merged: kept ${r.data.canonical_id} · removed ${r.data.removed.length} · moved ${r.data.projects_moved} projects`);
            run();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <button
                    data-testid="run-ai-detect"
                    onClick={run}
                    disabled={busy}
                    className="bg-[var(--brand-amber)] text-black px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                    <Sparkles size={12} /> {busy ? "Claude is analyzing…" : "Run AI Duplicate Detection"}
                </button>
            </div>

            {result && (
                <>
                    <div className="text-xs text-[var(--text-primary)]/70 font-mono-fin">
                        Provider: <span className="text-[var(--brand-amber)]">{result.provider}</span> ·
                        Analyzed: {result.analyzed_count || "—"} clients ·
                        Clusters: {result.clusters.length}
                    </div>

                    {result.clusters.length === 0 && (
                        <Panel testId="no-clusters" title="No merges suggested">
                            <div className="flex items-center gap-2 text-[var(--pos)]">
                                <CheckCircle size={16} />
                                <span className="text-[var(--text-primary)]/90">
                                    Claude found no confident duplicates. Your master data may already be clean, or the residual name variance is intentional (e.g., different projects for the same person).
                                </span>
                            </div>
                        </Panel>
                    )}

                    {result.clusters.map((cluster, i) => (
                        <Panel key={i} testId={`cluster-${i}`} title={`Cluster ${i + 1} · ${cluster.canonical_name}`} subtitle={`Confidence ${(cluster.confidence * 100).toFixed(0)}%`}>
                            <div className="text-[11px] text-[var(--text-primary)]/70 mb-2">
                                <span className="text-[var(--brand-amber)]">Reasoning:</span> {cluster.reasoning}
                            </div>
                            <div className="text-xs">
                                <div className="text-[10px] font-mono-fin uppercase text-[var(--text-secondary)] mb-1">Merging clients:</div>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {cluster.member_ids.map((mid) => (
                                        <span key={mid} className="border border-panel px-2 py-1 font-mono-fin text-[10px] bg-[var(--bg-base)]">
                                            {mid}
                                        </span>
                                    ))}
                                </div>
                                <button
                                    data-testid={`apply-merge-${i}`}
                                    onClick={() => applyMerge(cluster)}
                                    className="border border-[var(--brand-amber)] text-[var(--brand-amber)] px-3 py-1 text-[10px] font-mono-fin uppercase tracking-wider hover:bg-[var(--brand-amber)] hover:text-black transition-colors flex items-center gap-1"
                                >
                                    <Merge size={10} /> Apply Merge
                                </button>
                            </div>
                        </Panel>
                    ))}
                </>
            )}
        </div>
    );
}

function BulkPanTab() {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/enrich/candidates-missing-pan-gstin");
            setRows(r.data.items.map(it => ({ ...it, _dirty: false })));
        } catch (e) {
            toast.error(e.message);
        }
    };

    useEffect(() => { load(); }, []);

    const update = (id, key, val) => {
        setRows(rows.map(r => r.id === id ? { ...r, [key]: val, _dirty: true } : r));
    };

    const saveAll = async () => {
        const patches = rows
            .filter(r => r._dirty)
            .map(r => ({
                client_id: r.id, pan: r.pan, gstin: r.gstin,
                phone: r.phone, email: r.email,
            }));
        if (patches.length === 0) {
            toast.error("No changes to save");
            return;
        }
        setBusy(true);
        try {
            const r = await api.post("/enrich/bulk-update", { patches });
            toast.success(`Saved ${r.data.updated.length} client(s)`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <div className="text-xs text-[var(--text-primary)]/80 font-mono-fin">
                    <span className="text-[var(--brand-amber)]">{rows.length}</span> clients missing PAN / GSTIN
                </div>
                <button
                    data-testid="save-bulk"
                    onClick={saveAll}
                    disabled={busy || !rows.some(r => r._dirty)}
                    className="ml-auto bg-[var(--brand-amber)] text-black px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider hover:bg-[var(--bg-panel-hover)] transition-colors flex items-center gap-1.5 disabled:opacity-50"
                >
                    <Save size={12} /> Save {rows.filter(r => r._dirty).length} Changes
                </button>
            </div>

            <Panel testId="bulk-table">
                <div className="overflow-x-auto max-h-[600px]">
                    <table className="w-full text-xs font-body">
                        <thead className="sticky top-0 bg-panel">
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2 px-2">Client</th>
                                <th className="text-left py-2 px-2">PAN</th>
                                <th className="text-left py-2 px-2">GSTIN</th>
                                <th className="text-left py-2 px-2">Phone</th>
                                <th className="text-left py-2 px-2">Email</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`pan-row-${r.id}`} className={`border-b border-panel/60 ${r._dirty ? "bg-[var(--brand-amber)]/10" : ""}`}>
                                    <td className="py-1.5 px-2">
                                        <div className="text-[var(--text-primary)]/90">{r.name}</div>
                                        <div className="text-[10px] text-[var(--brand-amber)] font-mono-fin">{r.id}</div>
                                    </td>
                                    <td className="py-1 px-2">
                                        <input
                                            data-testid={`pan-input-${r.id}`}
                                            type="text"
                                            value={r.pan || ""}
                                            onChange={(e) => update(r.id, "pan", e.target.value.toUpperCase())}
                                            maxLength={10}
                                            placeholder="ABCDE1234F"
                                            className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-[11px] font-mono-fin uppercase focus:border-[var(--brand-amber)] focus:outline-none"
                                        />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input
                                            data-testid={`gstin-input-${r.id}`}
                                            type="text"
                                            value={r.gstin || ""}
                                            onChange={(e) => update(r.id, "gstin", e.target.value.toUpperCase())}
                                            maxLength={15}
                                            placeholder="27ABCDE1234F1Z5"
                                            className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-[11px] font-mono-fin uppercase focus:border-[var(--brand-amber)] focus:outline-none"
                                        />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input
                                            type="text"
                                            value={r.phone || ""}
                                            onChange={(e) => update(r.id, "phone", e.target.value)}
                                            placeholder="+91 90040 XXXXX"
                                            className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-[11px] font-mono-fin focus:border-[var(--brand-amber)] focus:outline-none"
                                        />
                                    </td>
                                    <td className="py-1 px-2">
                                        <input
                                            type="email"
                                            value={r.email || ""}
                                            onChange={(e) => update(r.id, "email", e.target.value)}
                                            placeholder="name@client.com"
                                            className="w-full bg-[var(--bg-base)] border border-panel px-2 py-1 text-[11px] font-body focus:border-[var(--brand-amber)] focus:outline-none"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Panel>
        </div>
    );
}

export default function EnrichMasterData() {
    const [tab, setTab] = useState("duplicates");
    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// SECTION 28 · ENRICH MASTER DATA</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <Sparkles className="text-[var(--brand-amber)]" size={26} />
                    Enrich Master Data
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    AI-powered duplicate merger + PAN/GSTIN bulk-fill → lift Data Quality grade D → A
                </p>
            </div>

            <div className="flex border-b border-panel">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.key}
                            data-testid={`tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`px-4 py-2 text-xs font-mono-fin uppercase tracking-wider flex items-center gap-2 border-b-2 ${tab === t.key ? "border-[var(--brand-amber)] text-[var(--brand-amber)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                        >
                            <Icon size={12} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "duplicates" && <AiDuplicateTab />}
            {tab === "panfill" && <BulkPanTab />}
        </div>
    );
}
